import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { formatListName } from "@/shared/utils/formatListName";
import { storeOf } from "./watchlistStore";

interface WatchlistTermsModalProps {
  /** Record id, unique only within its own store. */
  id: string | null;
  /**
   * Which store the id belongs to — the filter panel's section id, e.g.
   * `ticker-watchlists-filter` or `advanced-filters-highlight`. Required to
   * read the id correctly; see the note on the component.
   */
  sectionId?: string;
  onClose: () => void;
}

const tagIdToName: Record<string, string> = {};
menuCategories.forEach((category) => {
  category.items.forEach((item) => {
    tagIdToName[String(item.id)] = item.title;
  });
});

/**
 * What a watchlist actually contains, from the filter panel.
 *
 * The panel lists watchlists by name only, so choosing between "Fixed Income
 * Futures Proxies" and "Index Futures Terms" meant remembering what you put in
 * them — or leaving the widget to go and look. The terms are the thing being
 * decided about, so they belong one click away from the checkbox.
 *
 * Terms are read from the stores rather than carried on the row: the filter
 * menus describe options as id + label only, and threading contents through
 * six panel builders to reach this dialog would couple every one of them to a
 * display concern. These queries are already warm from the rest of the app.
 */
export default function WatchlistTermsModal({
  id,
  sectionId,
  onClose,
}: WatchlistTermsModalProps) {
  const { portfolios } = usePortfoliosCRUD();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();

  /**
   * Resolved within the one store the section names.
   *
   * This used to search all three in a fixed order and take the first hit,
   * on the reasoning that "an id that resolves is better evidence than a
   * label we inferred a type from". That reasoning was wrong: the three
   * stores have independent id sequences, so a hit in the first store proves
   * nothing about the row that was clicked. In practice a tag watchlist
   * opened whichever portfolio happened to share its number — clicking "Gold
   * Watch" showed the contents of "War".
   *
   * Falls back to the old scan only when no section is supplied, which no
   * caller now does.
   */
  const bare = (id ?? "").replace(/^tag-/, "");
  const store = storeOf(sectionId);
  const byId = <T extends { id: number | string | undefined }>(rows: T[]) =>
    rows.find((r) => String(r.id) === bare);

  const allPortfolios = portfolios;
  const allTags = watchlistsQuery.data ?? [];
  const allQueries = queriesQuery.data ?? [];

  const portfolio =
    store === "portfolio" || store === null ? byId(allPortfolios) : undefined;
  const tagWatchlist =
    store === "tag" || (store === null && !portfolio) ? byId(allTags) : undefined;
  const combined =
    store === "combined" || (store === null && !portfolio && !tagWatchlist)
      ? byId(allQueries)
      : undefined;

  let title = "";
  // Desk-authored, customer-facing. when_to_use is never surfaced here.
  let description: string | null | undefined;
  let sections: { label: string; values: string[] }[] = [];

  if (portfolio) {
    title = formatListName(portfolio.name);
    description = portfolio.description;
    sections = [
      { label: "Tickers", values: portfolio.tickers ?? [] },
      { label: "Keywords", values: portfolio.keywords ?? [] },
    ];
  } else if (!tagWatchlist && store === "tag" && tagIdToName[bare]) {
    /**
     * A single Newsquawk tag, not a watchlist.
     *
     * The tag-watchlists section carries two different kinds of row under one
     * bare-number namespace: the user's tag watchlists, and every individual
     * tag from menuCategories. Looking the id up as a watchlist therefore
     * found nothing for the tags, and the dialog opened empty with its
     * fallback title — which is what made every tag look broken.
     *
     * A single tag has no contents to list; it *is* the term. Saying so beats
     * an empty panel implying something failed to load.
     */
    title = tagIdToName[bare];
    sections = [{ label: "Tag", values: [tagIdToName[bare]] }];
  } else if (tagWatchlist) {
    title = formatListName(tagWatchlist.name);
    description = tagWatchlist.description;
    sections = [
      {
        label: "Tags",
        values: tagWatchlist.tags.map((tag) => tagIdToName[tag] ?? `Tag #${tag}`),
      },
    ];
  } else if (combined) {
    title = formatListName(combined.queryName);
    description = combined.description;
    sections = [
      {
        label: "Watchlists",
        values: (combined.portfolios ?? []).map((p) => formatListName(p.name)),
      },
      {
        label: "Tags",
        values: (combined.categories ?? []).map(
          (tag) => tagIdToName[String(tag)] ?? `Tag #${tag}`
        ),
      },
      {
        label: "Keywords",
        values: combined.searchKeyword ? [combined.searchKeyword] : [],
      },
    ];
  }

  const populated = sections.filter((section) => section.values.length > 0);

  return (
    <Dialog open={!!id} onClose={onClose} className="no-drag relative z-[60]">
      <DialogBackdrop className="fixed inset-0 bg-black/60" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
          <div className="flex items-center gap-3 border-b border-gray-700/50 bg-gray-800 px-4 py-2.5">
            <h2 className="flex-1 truncate text-sm font-semibold text-gray-100">
              {title || "Watchlist"}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-gray-400 transition-colors hover:text-gray-200"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
            {description && (
              <p className="text-xs leading-relaxed text-gray-400">{description}</p>
            )}
            {populated.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nothing to show for this one — it may be a Newsquawk list whose
                contents load with the feed.
              </p>
            ) : (
              populated.map((section) => (
                <div key={section.label}>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {section.label}
                    </h3>
                    <span className="font-mono text-xs tabular-nums text-gray-600">
                      {section.values.length}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {section.values.map((value) => (
                      <span
                        key={value}
                        className="rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 text-xs text-gray-300"
                      >
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
